import { useUnsavedChanges, useConfirmLeave } from './UnsavedChanges';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, downloadPrivateFile, useList } from '@yingbaba/api';
import {
  Button,
  DatePicker,
  ErrorNotice,
  Field,
  Select,
  Modal,
  Notice,
  useToast,
} from '@yingbaba/ui';
import { Download, Pencil, Check } from '@yingbaba/icons';
import { date, money } from '@yingbaba/utils';
import { BriefLicensePicker, BriefLicenseRefs } from './BriefLicenses';
import type {
  Project,
  ProjectBrief,
  ProjectRightsItem,
  Artifact,
  ProjectFile,
  ProjectLicenseEvidence,
} from '@yingbaba/types';

import { briefFields, planBriefImport, type BriefImportPlan } from './project-brief-import';

export const rightsKinds = [
  ['music', '音乐与音效'],
  ['footage', '视频素材'],
  ['fonts', '字体'],
  ['images', '图片与插画'],
  ['likeness', '肖像与商标'],
  ['other', '其他权利'],
] as const;
export const rightsStatuses: Record<string, string> = {
  pending: '待核对',
  commercial: '已核对可商用',
  restricted: '使用受限',
  not_used: '不涉及',
};
const normalize = (brief?: ProjectBrief) =>
  ({
    ...brief,
    open_questions: brief?.open_questions || [],
    rights: rightsKinds.map(
      ([kind]) =>
        brief?.rights?.find((r) => r.kind === kind) || {
          kind,
          status: 'pending',
          source: '',
          scope: '',
          evidence_file_id: '',
          notes: '',
        },
    ),
  }) as ProjectBrief;

export function ProjectBriefPanel({ project, write }: { project: Project; write: boolean }) {
  const [editing, setEditing] = useState(false),
    [brief, setBrief] = useState(() => normalize(project.brief)),
    [deadline, setDeadline] = useState(project.deadline || ''),
    [revision, setRevision] = useState(project.revision);
  const [busy, setBusy] = useState(false),
    [exporting, setExporting] = useState(''),
    [error, setError] = useState<unknown>(null),
    [dirty, setDirty] = useState(false);
  const [reload, setReload] = useState(false);
  const [licenseIndex, setLicenseIndex] = useState(-1),
    [pickedEvidence, setPickedEvidence] = useState<ProjectLicenseEvidence[]>([]);
  const licenseEvidence = [
    ...new Map(
      [...pickedEvidence, ...(project.brief_license_evidence || [])].map((v) => [v.id, v]),
    ).values(),
  ];
  const [showAI, setShowAI] = useState(false),
    [aiID, setAIID] = useState('');
  const [filePage, setFilePage] = useState(1),
    [fileOptions, setFileOptions] = useState<ProjectFile[]>([]);
  const projectFiles = useList<ProjectFile>(
    `/projects/${project.id}/files?page=${filePage}`,
    editing,
  );
  useEffect(() => {
    if (projectFiles.data?.items)
      setFileOptions((old) => [
        ...new Map([...old, ...projectFiles.data.items].map((f) => [f.id, f])).values(),
      ]);
  }, [projectFiles.data]);
  const ai = useList<Artifact>('/workspace/artifacts?kind=brief&page_size=100', showAI);
  const qc = useQueryClient(),
    toast = useToast();
  useEffect(() => {
    if (!editing) {
      setBrief(normalize(project.brief));
      setDeadline(project.deadline || '');
      setRevision(project.revision);
    }
  }, [project.revision, editing]);
  const leave = useConfirmLeave();
  const discard = () => {
    setEditing(false);
    setDirty(false);
    setError(null);
  };
  useUnsavedChanges({ dirty, busy, save: persist, discard });
  function change(field: string, value: unknown) {
    if (JSON.stringify(brief[field as keyof ProjectBrief]) === JSON.stringify(value)) return;
    setBrief((b) => ({ ...b, [field]: value }));
    setDirty(true);
  }
  function rightChange(index: number, field: string, value: string | string[]) {
    change(
      'rights',
      brief.rights.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    );
  }
  async function persist(): Promise<boolean> {
    if (busy) return false;
    setBusy(true);
    setError(null);
    try {
      await api(`/projects/${project.id}/brief`, {
        method: 'PUT',
        body: JSON.stringify({ brief, deadline, expected_revision: revision }),
      });
      setDirty(false);
      setEditing(false);
      await qc.invalidateQueries();
      toast('创作需求已保存');
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function download(format: string) {
    if (exporting) return;
    setExporting(format);
    setError(null);
    try {
      await downloadPrivateFile(
        `/projects/${project.id}/brief/export?format=${format}`,
        `${project.title}-创作需求书.${format}`,
        { direct: false },
      );
    } catch (e) {
      setError(e);
    } finally {
      setExporting('');
    }
  }
  const aiCandidates =
    ai.data?.items.filter(
      (v) =>
        v.kind === 'brief' &&
        v.structured_content &&
        ['draft', 'succeeded', 'locked'].includes(v.status),
    ) || [];
  const selectedAI = aiCandidates.find((v) => v.id === aiID);
  let importPlan: BriefImportPlan | null = null;
  let importError: unknown = null;
  if (selectedAI) {
    try {
      importPlan = planBriefImport(brief, deadline, selectedAI.structured_content);
    } catch (e) {
      importError = e;
    }
  }
  const filled = briefFields.filter(([key]) => project.brief?.[key]?.trim()).length;
  const source = normalize(project.brief);
  return (
    <section className="panel project-brief-panel">
      <div className="panel-title">
        <div>
          <h2>创作需求</h2>
          <p className="muted small">把目标、内容和使用范围说明白，让每位参与者理解同一份需求。</p>
        </div>
        <div className="row wrap brief-header-actions">
          {!editing && write && (
            <Button
              variant="secondary"
              onClick={() => {
                setError(null);
                setEditing(true);
              }}
            >
              <Pencil size={15} />
              {filled ? '编辑需求' : '完善创作需求'}
            </Button>
          )}
          <Button
            variant="secondary"
            disabled={editing || !!exporting}
            loading={exporting === 'docx'}
            onClick={() => download('docx')}
          >
            <Download size={15} />
            Word
          </Button>
          <Button
            variant="secondary"
            disabled={editing || !!exporting}
            loading={exporting === 'pdf'}
            onClick={() => download('pdf')}
          >
            <Download size={15} />
            PDF
          </Button>
        </div>
      </div>
      <ErrorNotice error={error} />
      <BriefLicensePicker
        projectID={project.id}
        open={licenseIndex >= 0}
        value={brief.rights[licenseIndex]?.license_reference_ids || []}
        onOpenChange={(open) => {
          if (!open) setLicenseIndex(-1);
        }}
        onConfirm={(ids, evidence) => {
          rightChange(licenseIndex, 'license_reference_ids', ids);
          setPickedEvidence((old) => [
            ...new Map([...old, ...evidence].map((v) => [v.id, v])).values(),
          ]);
        }}
      />
      {error instanceof ApiError && error.code === 'PROJECT_CHANGED' && editing && (
        <Notice>
          你的草稿仍保留在此页。项目已更新，请先核对最新需求。
          <Button type="button" variant="ghost" onClick={() => setReload(true)}>
            载入最新需求
          </Button>
        </Notice>
      )}
      <Modal open={reload} onOpenChange={setReload} title="载入最新创作需求">
        <p>载入后，当前未保存的修改将被替换。如需保留，请先复制你填写的内容。</p>
        <div className="row wrap brief-form-actions">
          <Button variant="secondary" onClick={() => setReload(false)}>
            继续编辑草稿
          </Button>
          <Button
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await qc.refetchQueries(
                  { queryKey: [`/projects/${project.id}`] },
                  { throwOnError: true },
                );
                setEditing(false);
                setDirty(false);
                setError(null);
                setReload(false);
              } catch (e) {
                setError(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            载入最新需求
          </Button>
        </div>
      </Modal>
      {editing ? (
        <form
          className="form-stack"
          onSubmit={(e) => {
            e.preventDefault();
            void persist();
          }}
        >
          <div className="row spread wrap">
            <p className="small muted">尚未确定的内容可以留空，稍后继续完善。</p>
            <Button type="button" variant="ghost" onClick={() => setShowAI(!showAI)}>
              {showAI ? '收起草稿选择' : '从创作草稿补充'}
            </Button>
          </div>
          {showAI && (
            <div className="brief-import">
              <p className="small muted">
                选择 AI 创作中已保存的需求草稿，仅补充空白项。补充后请核对并保存。
              </p>
              <ErrorNotice error={ai.error || importError} />
              {!!ai.error && (
                <Button type="button" variant="secondary" onClick={() => void ai.refetch()}>
                  重新加载草稿
                </Button>
              )}
              {!ai.isPending && !ai.error && !aiCandidates.length && (
                <p className="small muted">
                  还没有可补充的需求草稿。可先在 AI 创作中整理并保存需求。
                </p>
              )}

              <Select
                aria-label="创作需求草稿"
                disabled={busy || ai.isPending}
                value={aiID}
                onChange={setAIID}
              >
                <option value="">选择已保存的需求草稿</option>
                {aiCandidates.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.title} · V{v.version}
                  </option>
                ))}
              </Select>
              {importPlan && (
                <div className="brief-import-review" aria-label="将补充的需求内容">
                  {importPlan.changes.length ? (
                    <>
                      <p className="small muted">
                        将补充 {importPlan.changes.length} 项：
                        {importPlan.changes.map((change) => change.label).join('、')}
                        。已有内容与版权核对记录保留。
                      </p>
                      <details>
                        <summary>查看补充内容</summary>
                        <dl>
                          {importPlan.changes.map((change) => (
                            <div key={change.label}>
                              <dt>{change.label}</dt>
                              <dd>{change.value}</dd>
                            </div>
                          ))}
                        </dl>
                      </details>
                    </>
                  ) : (
                    <p className="small muted">
                      没有可补充的空白项。可选择其他草稿，或直接编辑当前需求。
                    </p>
                  )}
                </div>
              )}
              <Button
                type="button"
                variant="secondary"
                disabled={!importPlan?.changes.length || busy}
                onClick={() => {
                  if (!importPlan?.changes.length || busy) return;
                  setBrief(importPlan.brief);
                  setDeadline(importPlan.deadline);
                  setDirty(true);
                  setError(null);
                  setShowAI(false);
                  toast(`已补充 ${importPlan.changes.length} 项，请核对并保存创作需求`);
                }}
              >
                补充到当前需求
              </Button>
            </div>
          )}
          <fieldset className="brief-fieldset form-stack" disabled={busy}>
            <div className="form-grid">
              {briefFields.slice(0, 4).map(([key, label, placeholder]) => (
                <Field key={key} label={label} htmlFor={`project-brief-${key}`}>
                  <textarea
                    id={`project-brief-${key}`}
                    value={brief[key] || ''}
                    onChange={(e) => change(key, e.target.value)}
                    placeholder={placeholder}
                    maxLength={4000}
                    rows={key === 'key_message' || key === 'constraints' ? 4 : 3}
                  />
                </Field>
              ))}
            </div>
            <details className="brief-edit-section">
              <summary>
                传播与制作要求 <span className="muted small">渠道、规格、风格与确认负责人</span>
              </summary>
              <div className="form-grid">
                {briefFields.slice(4).map(([key, label, placeholder]) => (
                  <Field key={key} label={label} htmlFor={`project-brief-${key}`}>
                    <textarea
                      id={`project-brief-${key}`}
                      value={brief[key] || ''}
                      onChange={(e) => change(key, e.target.value)}
                      placeholder={placeholder}
                      maxLength={4000}
                      rows={3}
                    />
                  </Field>
                ))}
              </div>
            </details>
            <div className="form-grid">
              <Field label="成片时长（秒）" htmlFor="brief-duration">
                <input
                  id="brief-duration"
                  className="input"
                  type="number"
                  min={0}
                  max={86400}
                  step={1}
                  value={brief.duration_seconds || ''}
                  placeholder="例如 90，可留空待确认"
                  onChange={(e) => change('duration_seconds', Number(e.target.value))}
                />
              </Field>
              <Field label="计划预算（元）" htmlFor="brief-budget">
                <input
                  id="brief-budget"
                  className="input"
                  type="number"
                  min={0}
                  max={1000000000}
                  step="0.01"
                  value={brief.budget_minor ? brief.budget_minor / 100 : ''}
                  placeholder="可留空待确认"
                  onChange={(e) => change('budget_minor', Math.round(Number(e.target.value) * 100))}
                />
              </Field>
              <Field label="交付日期" htmlFor="brief-deadline">
                <DatePicker
                  id="brief-deadline"
                  value={deadline}
                  onChange={(v) => {
                    if (v === deadline) return;
                    setDeadline(v);
                    setDirty(true);
                  }}
                />
              </Field>
            </div>
            <div>
              <h3>知识产权与使用范围</h3>
              <p className="muted small">
                逐项核对来源和许可范围。仅完成购买或下载，不能代替对实际用途的确认。
              </p>
            </div>
            <ErrorNotice error={projectFiles.error} />
            <div className="brief-rights-list">
              {brief.rights.map((r, i) => (
                <section className="brief-rights-card" key={r.kind}>
                  <div className="row spread wrap">
                    <h4>{rightsKinds.find(([key]) => key === r.kind)?.[1]}</h4>
                    <Select
                      aria-label={`${rightsKinds.find(([key]) => key === r.kind)?.[1]}授权状态`}
                      value={r.status}
                      onChange={(v) => rightChange(i, 'status', v)}
                    >
                      {Object.entries(rightsStatuses).map(([v, label]) => (
                        <option key={v} value={v}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </div>
                  {r.status !== 'not_used' && (
                    <details
                      className="brief-right-details"
                      open={
                        r.status === 'commercial' ||
                        r.status === 'restricted' ||
                        !!r.source ||
                        !!r.scope ||
                        !!r.notes ||
                        !!r.evidence_file_id ||
                        !!r.license_reference_ids?.length
                      }
                    >
                      <summary>来源、使用范围与凭证</summary>
                      <div className="form-grid">
                        <Field label="权利来源" htmlFor={`rights-source-${i}`}>
                          <textarea
                            id={`rights-source-${i}`}
                            required={r.status === 'commercial'}
                            rows={2}
                            maxLength={2000}
                            value={r.source}
                            onChange={(e) => rightChange(i, 'source', e.target.value)}
                            placeholder="作者、授权方、许可名称或订单依据"
                          />
                        </Field>
                        <Field label="允许的使用范围" htmlFor={`rights-scope-${i}`}>
                          <textarea
                            id={`rights-scope-${i}`}
                            required={r.status === 'commercial'}
                            rows={2}
                            maxLength={2000}
                            value={r.scope}
                            onChange={(e) => rightChange(i, 'scope', e.target.value)}
                            placeholder="使用主体、项目、渠道、地区、期限及限制"
                          />
                        </Field>
                        <Field label="补充说明" htmlFor={`rights-note-${i}`}>
                          <textarea
                            id={`rights-note-${i}`}
                            rows={2}
                            maxLength={2000}
                            value={r.notes}
                            onChange={(e) => rightChange(i, 'notes', e.target.value)}
                            placeholder="待补的授权、须注明署名等"
                          />
                        </Field>
                        <Field label="授权凭证（可选）">
                          <Select
                            value={r.evidence_file_id || ''}
                            onChange={(v) => rightChange(i, 'evidence_file_id', v)}
                          >
                            <option value="">暂不关联文件</option>
                            {r.evidence_file_id &&
                              !fileOptions.some((f) => f.id === r.evidence_file_id) && (
                                <option value={r.evidence_file_id}>已关联的项目凭证</option>
                              )}
                            {fileOptions.map((f) => (
                              <option key={f.id} value={f.id}>
                                {f.name} · V{f.version || 1}
                              </option>
                            ))}
                          </Select>
                        </Field>
                      </div>
                      <div className="brief-license-actions">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => setLicenseIndex(i)}
                        >
                          关联已购素材许可
                        </Button>
                        <p className="small muted">
                          选择本项目已添加的许可，保存与导出时会核对其当前状态。
                        </p>
                      </div>
                      <BriefLicenseRefs ids={r.license_reference_ids} evidence={licenseEvidence} />
                      {!!r.license_reference_ids?.length && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => rightChange(i, 'license_reference_ids', [])}
                        >
                          清除许可关联
                        </Button>
                      )}
                    </details>
                  )}
                </section>
              ))}
            </div>
            {projectFiles.data && filePage * 30 < projectFiles.data.total && (
              <Button
                type="button"
                variant="ghost"
                disabled={projectFiles.isPending}
                onClick={() => setFilePage((p) => p + 1)}
              >
                加载更多项目凭证文件
              </Button>
            )}
            <Field label="待确认事项（每行一项）" htmlFor="brief-open-questions">
              <textarea
                id="brief-open-questions"
                rows={4}
                maxLength={15000}
                value={brief.open_questions.join('\n')}
                onChange={(e) => change('open_questions', e.target.value.split('\n'))}
                onBlur={() =>
                  change(
                    'open_questions',
                    brief.open_questions.map((v) => v.trim()).filter(Boolean),
                  )
                }
              />
            </Field>
          </fieldset>
          <div className="row wrap brief-form-actions">
            <Button loading={busy} type="submit">
              <Check size={15} />
              保存创作需求
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => leave(discard)}
            >
              取消
            </Button>
            {dirty && <span className="muted small">有未保存的修改</span>}
          </div>
        </form>
      ) : (
        <>
          <dl className="brief-summary-facts">
            <div>
              <dt>目标人群</dt>
              <dd>{source.audience || '待确认'}</dd>
            </div>
            <div>
              <dt>成片时长</dt>
              <dd>{source.duration_seconds ? `${source.duration_seconds} 秒` : '待确认'}</dd>
            </div>
            <div>
              <dt>交付日期</dt>
              <dd>{date(project.deadline)}</dd>
            </div>
            <div>
              <dt>计划预算</dt>
              <dd>{source.budget_minor ? money(source.budget_minor) : '待确认'}</dd>
            </div>
          </dl>
          {!briefFields.some(([key]) => key !== 'audience' && source[key]?.trim()) && (
            <p className="muted small">
              品牌背景、表达内容和制作规格尚未记录，完善需求后会显示在这里。
            </p>
          )}
          <div className="brief-read-sections">
            {briefFields
              .filter(([key]) => key !== 'audience' && source[key]?.trim())
              .map(([key, label]) => (
                <section key={key}>
                  <h3>{label}</h3>
                  <p className={`prose ${source[key] ? '' : 'muted'}`}>{source[key] || '待确认'}</p>
                </section>
              ))}
          </div>
          <section>
            <h3>知识产权与使用范围</h3>
            <div className="brief-rights-list">
              {source.rights.map((r) => (
                <div className="brief-rights-card" key={r.kind}>
                  <div className="row spread wrap">
                    <strong>{rightsKinds.find(([key]) => key === r.kind)?.[1]}</strong>
                    <span
                      className={`tag ${r.status === 'commercial' && !(r.license_reference_ids || []).some((id) => !project.brief_license_evidence?.find((v) => v.id === id)?.available) ? 'tag-success' : ''}`}
                    >
                      {r.status === 'commercial' &&
                      (r.license_reference_ids || []).some(
                        (id) =>
                          !project.brief_license_evidence?.find((v) => v.id === id)?.available,
                      )
                        ? '需重新核对'
                        : rightsStatuses[r.status] || '待核对'}
                    </span>
                  </div>
                  {r.status !== 'not_used' && r.source && (
                    <p className="prose small">来源：{r.source}</p>
                  )}
                  {r.status !== 'not_used' && r.scope && (
                    <p className="prose small">范围：{r.scope}</p>
                  )}
                  {r.status !== 'not_used' && r.notes && <p className="prose small">{r.notes}</p>}
                  {r.status !== 'not_used' && (
                    <BriefLicenseRefs
                      ids={r.license_reference_ids}
                      evidence={project.brief_license_evidence}
                    />
                  )}
                </div>
              ))}
            </div>
          </section>
          {!!source.open_questions.length && (
            <section>
              <h3>待确认事项</h3>
              <ul className="brief-questions">
                {source.open_questions.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </section>
  );
}
